import { POLONIA_CREST as logoUrl } from './clubIdentity';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, Check, Edit3, Expand, ImagePlus, Plus, Save, Trash2, X } from 'lucide-react';
import { useI18n } from './I18nContext';
import type { Match, Player } from './types';
import { playerOverall, playerStats } from './playerUtils';
import { useData } from './DataContext';
import { staffForPlayer } from './staffAccess';

type Tab = 'overview' | 'attributes' | 'mental' | 'abilities';
type NumericMap = Record<string, number>;

const ATTRIBUTE_TEMPLATE: NumericMap = {
  acceleration: 50,
  sprint_speed: 50,
  agility: 50,
  balance: 50,
  stamina: 50,
  strength: 50,
  ball_control: 50,
  first_touch: 50,
  dribbling: 50,
  short_passing: 50,
  long_passing: 50,
  crossing: 50,
  vision: 50,
  finishing: 50,
  shot_power: 50,
  long_shots: 50,
  heading: 50,
  tackling: 50,
  interceptions: 50,
  positioning: 50,
};

const MENTAL_TEMPLATE: NumericMap = {
  anticipation: 50,
  decisions: 50,
  composure: 50,
  concentration: 50,
  confidence: 50,
  teamwork: 50,
  leadership: 50,
  work_rate: 50,
  aggression: 50,
  discipline: 50,
  determination: 50,
  adaptability: 50,
};

const ABILITY_TEMPLATE: NumericMap = {
  pressing: 50,
  counter_attacking: 50,
  hold_up_play: 50,
  aerial_threat: 50,
  one_touch_play: 50,
  through_balls: 50,
  ball_retention: 50,
  set_pieces: 50,
  recovery_runs: 50,
  versatility: 50,
};

function templateFor(tab: Tab): NumericMap {
  if (tab === 'attributes') return { ...ATTRIBUTE_TEMPLATE };
  if (tab === 'mental') return { ...MENTAL_TEMPLATE };
  if (tab === 'abilities') return { ...ABILITY_TEMPLATE };
  return {};
}

function entries(values?: Record<string, number> | null) {
  return Object.entries(values ?? {}).sort((a, b) => b[1] - a[1]);
}

function label(value: string) {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
}

function mapFrom(entriesValue: Array<[string, number]>): NumericMap {
  return Object.fromEntries(entriesValue.filter(([key]) => key.trim()).map(([key, value]) => [key.trim(), clamp(Number(value), 0, 100)]));
}

function EditableMetricBoard({
  values,
  onChange,
  emptyText,
  onLoadTemplate,
}: {
  values: NumericMap;
  onChange: (next: NumericMap) => void;
  emptyText: string;
  onLoadTemplate?: () => void;
}) {
  const rows = Object.entries(values);

  function rename(oldKey: string, newKey: string) {
    const trimmed = newKey.trim().toLowerCase().replace(/\s+/g, '_');
    if (!trimmed || trimmed === oldKey) return;
    const next: NumericMap = {};
    Object.entries(values).forEach(([key, value]) => {
      next[key === oldKey ? trimmed : key] = value;
    });
    onChange(next);
  }

  return (
    <section className="attribute-board editable-attribute-board">
      {rows.length ? rows.map(([name, value]) => (
        <div className="editable-attribute-row" key={name}>
          <input
            className="attribute-name-input"
            defaultValue={label(name)}
            onBlur={(event) => rename(name, event.target.value)}
            aria-label={label(name)}
          />
          <input
            type="range"
            min="0"
            max="100"
            value={value}
            onChange={(event) => onChange({ ...values, [name]: Number(event.target.value) })}
          />
          <input
            className="attribute-value-input"
            type="number"
            min="0"
            max="100"
            value={value}
            onFocus={(event) => event.currentTarget.select()}
            onChange={(event) => onChange({ ...values, [name]: clamp(Number(event.target.value), 0, 100) })}
          />
          <button
            type="button"
            className="attribute-remove-button"
            onClick={() => {
              const next = { ...values };
              delete next[name];
              onChange(next);
            }}
            aria-label="Remove"
          >
            <X size={14} />
          </button>
        </div>
      )) : (
        <div className="empty-metric-editor">
          <p>{emptyText}</p>
          {onLoadTemplate && (
            <button type="button" className="gold-button load-metric-template" onClick={onLoadTemplate}>
              <Plus size={15} />
              Load standard football metrics
            </button>
          )}
          <small>Template values start at 50 as neutral placeholders. Edit them before saving.</small>
        </div>
      )}

      <button
        type="button"
        className="add-metric-button"
        onClick={() => {
          let index = 1;
          let key = 'new_metric';
          while (key in values) {
            index += 1;
            key = `new_metric_${index}`;
          }
          onChange({ ...values, [key]: 50 });
        }}
      >
        <Plus size={15} />
        Add metric
      </button>
    </section>
  );
}


async function preparePlayerPhoto(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) throw new Error('Please select an image file');
  if (file.size > 20 * 1024 * 1024) throw new Error('Image is too large (maximum 20 MB)');

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('This image format could not be read'));
      image.src = objectUrl;
    });

    const maxDimension = 1600;
    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * scale));
    const height = Math.max(1, Math.round(image.naturalHeight * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Image processing is unavailable');

    context.drawImage(image, 0, 0, width, height);

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => blob ? resolve(blob) : reject(new Error('Could not prepare image')),
        'image/jpeg',
        0.88,
      );
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export function PlayerSheet({
  player,
  matches,
  onClose,
}: {
  player: Player;
  matches: Match[];
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { updatePlayer, uploadPlayerPhoto, deletePlayer } = useData();

  const [tab, setTab] = useState<Tab>('overview');
  const [editing, setEditing] = useState(false);
  const [savedPlayer, setSavedPlayer] = useState<Player>(player);
  const [draft, setDraft] = useState<Player>(player);
  const [saveMessage, setSaveMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoMessage, setPhotoMessage] = useState('');
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const sheetRef = useRef<HTMLElement | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);

  useEffect(() => {
    if (!editing) {
      setSavedPlayer(player);
      setDraft(player);
    }
  }, [player, editing]);

  const activePlayer = editing ? draft : savedPlayer;
  const staffRole = staffForPlayer(activePlayer.name);
  const stats = useMemo(() => playerStats(matches, activePlayer.id), [matches, activePlayer.id]);
  const bio = (activePlayer.bio ?? {}) as Record<string, any>;
  const overall = playerOverall(activePlayer);
  const averageRating = stats.ratedMatches
    ? (stats.ratingTotal / stats.ratedMatches).toFixed(1)
    : '—';

  const tabs: Array<[Tab, string]> = [
    ['overview', t.overview],
    ['attributes', t.attributes],
    ['mental', t.mental],
    ['abilities', t.specialAbilities],
  ];

  const currentEntries =
    tab === 'attributes'
      ? entries(activePlayer.attributes)
      : tab === 'mental'
        ? entries(activePlayer.mindset)
        : tab === 'abilities'
          ? entries(activePlayer.abilities)
          : [];

  function setBioField(key: string, value: unknown) {
    setDraft({
      ...draft,
      bio: {
        ...(draft.bio ?? {}),
        [key]: value,
      },
    });
  }

  function beginEdit() {
    setDraft(savedPlayer);
    setSaveMessage('');
    setConfirmDelete(false);
    setEditing(true);
  }

  function cancelEdit() {
    setDraft(savedPlayer);
    setSaveMessage('');
    setEditing(false);
  }

  async function handlePhotoFile(file?: File) {
    if (!file) return;
    setPhotoUploading(true);
    setPhotoMessage('');

    try {
      const prepared = await preparePlayerPhoto(file);
      const result = await uploadPlayerPhoto(savedPlayer.id, prepared, 'jpg');

      if (!result.ok || !result.photoUrl) {
        setPhotoMessage(result.message || 'Photo upload failed');
        return;
      }

      setDraft((current) => ({ ...current, photo_url: result.photoUrl ?? null }));
      setPhotoMessage('Photo ready — tap Save Changes');
    } catch (error) {
      setPhotoMessage(error instanceof Error ? error.message : 'Photo upload failed');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  }

  async function saveEdit() {
    setSaving(true);
    setSaveMessage('');

    const result = await updatePlayer(savedPlayer.id, {
      name: draft.name,
      squad_number: draft.squad_number,
      position: draft.position,
      potential: clamp(Number(draft.potential ?? 0), 0, 99),
      current_form: clamp(Number(draft.current_form ?? 0), 0, 10),
      fitness: clamp(Number(draft.fitness ?? 0), 0, 100),
      morale: clamp(Number(draft.morale ?? 0), 0, 100),
      photo_url: draft.photo_url,
      bio: draft.bio ?? {},
      notes: draft.notes ?? '',
      attributes: mapFrom(entries(draft.attributes)),
      mindset: mapFrom(entries(draft.mindset)),
      abilities: mapFrom(entries(draft.abilities)),
    });

    setSaving(false);
    if (!result.ok) {
      setSaveMessage(result.message);
      return;
    }

    const next = result.player ?? draft;
    setSavedPlayer(next);
    setDraft(next);
    setSaveMessage(t.playerUpdated);
    setEditing(false);
  }

  return (
    <div className="player-sheet-backdrop" onClick={onClose}>
      <article ref={sheetRef} className={`player-sheet metal-card ${editing ? 'player-sheet-editing' : ''}`} onClick={(event) => event.stopPropagation()}>
        <div className="sheet-top-actions">
          {!editing ? (
            <button className="sheet-edit-button" onClick={beginEdit} type="button">
              <Edit3 size={17} />
              {t.edit}
            </button>
          ) : (
            <>
              <button className="steel-button sheet-cancel-edit" onClick={cancelEdit} type="button">
                <X size={16} />
                {t.cancel}
              </button>
              <button className="gold-button sheet-save-edit" onClick={saveEdit} disabled={saving || photoUploading || !draft.name.trim()} type="button">
                <Save size={16} />
                {saving ? `${t.saving}…` : t.saveChanges}
              </button>
            </>
          )}
          <button className="sheet-close steel-button" onClick={onClose} aria-label={t.close}>
            <X size={20} />
          </button>
        </div>

        <header className="sheet-hero">
          <div className="sheet-photo">
            {(editing ? draft.photo_url : activePlayer.photo_url) ? (
              <button
                type="button"
                className="sheet-photo-preview-button"
                onClick={() => !editing && setPhotoViewerOpen(true)}
                aria-label={editing ? activePlayer.name : `Open ${activePlayer.name} photo`}
                disabled={editing}
              >
                <img src={(editing ? draft.photo_url : activePlayer.photo_url) ?? ''} alt={activePlayer.name} />
                {!editing && <span className="sheet-photo-expand-hint"><Expand size={13}/> View photo</span>}
              </button>
            ) : <img src={logoUrl} alt="" />}
            {editing && (
              <>
                <input
                  ref={photoInputRef}
                  className="player-photo-file-input"
                  type="file"
                  accept="image/*"
                  onChange={(event) => void handlePhotoFile(event.target.files?.[0])}
                  aria-label="Choose player photo"
                />
                <button
                  className="player-photo-upload-button"
                  type="button"
                  disabled={photoUploading}
                  onClick={() => photoInputRef.current?.click()}
                >
                  {photoUploading ? <span className="photo-upload-spinner" /> : <Camera size={16} />}
                  <span>{photoUploading ? 'Uploading…' : 'Change photo'}</span>
                </button>
              </>
            )}
            {editing ? (
              <select
                className="sheet-position-select"
                value={draft.position}
                onChange={(event) => setDraft({ ...draft, position: event.target.value })}
              >
                {['GK','RB','RCB','CB','LCB','LB','RWB','LWB','CDM','CM','CAM','RM','LM','RW','LW','CF','ST','TBA'].map((position) => (
                  <option key={position}>{position}</option>
                ))}
              </select>
            ) : <span>{activePlayer.position || '—'}</span>}
          </div>

          <div>
            <p className="eyebrow">{editing ? t.editPlayer : t.playerSheet}</p>
            {editing ? (
              <input
                className="sheet-name-input"
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            ) : <h1>{activePlayer.name}</h1>}

            {staffRole?.role === 'head_coach' && (
              <div className="player-staff-badge">
                <span>{t.headCoach}</span>
                <b>+</b>
                <span>{t.player}</span>
              </div>
            )}

            <div className="sheet-tags">
              {editing ? (
                <>
                  <label>
                    <span>#</span>
                    <input
                      type="number"
                      min="0"
                      max="99"
                      value={draft.squad_number ?? ''}
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(event) => setDraft({
                        ...draft,
                        squad_number: event.target.value === '' ? null : Number(event.target.value),
                      })}
                      aria-label={t.squadNumber}
                    />
                  </label>
                  <label>
                    <span>{t.potential}</span>
                    <input
                      type="number"
                      min="0"
                      max="99"
                      value={draft.potential ?? ''}
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(event) => setDraft({
                        ...draft,
                        potential: event.target.value === '' ? null : Number(event.target.value),
                      })}
                    />
                  </label>
                  <label>
                    <span>{t.form}</span>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step=".1"
                      value={draft.current_form ?? ''}
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(event) => setDraft({
                        ...draft,
                        current_form: event.target.value === '' ? null : Number(event.target.value),
                      })}
                    />
                  </label>
                </>
              ) : (
                <>
                  <span>#{activePlayer.squad_number ?? '—'}</span>
                  <span>{t.potential} {activePlayer.potential ?? '—'}</span>
                  <span>{t.form} {Number(activePlayer.current_form ?? 0).toFixed(1)}</span>
                </>
              )}
            </div>
          </div>

          <div className="sheet-overall">
            <strong>{overall}</strong>
            <span>OVR</span>
          </div>
        </header>

        {editing && (
          <section className="universal-player-edit-strip">
            <div className="player-photo-edit-control">
              <span>Player photo</span>
              <div className="player-photo-edit-actions">
                <button
                  className="steel-button"
                  type="button"
                  disabled={photoUploading}
                  onClick={() => photoInputRef.current?.click()}
                >
                  <ImagePlus size={15} />
                  {photoUploading ? 'Uploading…' : 'Upload from device'}
                </button>
                {draft.photo_url && (
                  <button
                    className="steel-button subtle-danger"
                    type="button"
                    disabled={photoUploading}
                    onClick={() => setDraft({ ...draft, photo_url: null })}
                  >
                    <Trash2 size={15} />
                    Remove
                  </button>
                )}
              </div>
              {photoMessage && <small className="player-photo-upload-message">{photoMessage}</small>}
              <details className="player-photo-url-fallback">
                <summary>Advanced: use image URL</summary>
                <input
                  value={draft.photo_url ?? ''}
                  onChange={(event) => setDraft({ ...draft, photo_url: event.target.value || null })}
                  placeholder="https://…"
                />
              </details>
            </div>
            <label>
              <span>{t.fitness}</span>
              <input
                type="number"
                min="0"
                max="100"
                value={draft.fitness ?? ''}
                onFocus={(event) => event.currentTarget.select()}
                onChange={(event) => setDraft({
                  ...draft,
                  fitness: event.target.value === '' ? null : Number(event.target.value),
                })}
              />
            </label>
            <label>
              <span>{t.morale}</span>
              <input
                type="number"
                min="0"
                max="100"
                value={draft.morale ?? ''}
                onFocus={(event) => event.currentTarget.select()}
                onChange={(event) => setDraft({
                  ...draft,
                  morale: event.target.value === '' ? null : Number(event.target.value),
                })}
              />
            </label>
          </section>
        )}

        <nav className="sheet-tabs">
          {tabs.map(([id, text]) => (
            <button
              key={id}
              className={tab === id ? 'active' : ''}
              type="button"
              onClick={() => {
                setTab(id);
                window.requestAnimationFrame(() => {
                  const sheet = sheetRef.current;
                  if (!sheet) return;
                  const tabsNode = sheet.querySelector('.sheet-tabs') as HTMLElement | null;
                  const boardNode = tabsNode?.nextElementSibling as HTMLElement | null;
                  if (boardNode) {
                    const target = Math.max(0, boardNode.offsetTop - 120);
                    sheet.scrollTo({ top: target, behavior: 'smooth' });
                  }
                });
              }}
            >
              {text}
            </button>
          ))}
        </nav>

        {tab === 'overview' ? (
          <div className="sheet-overview-grid">
            <section className="sheet-stat-grid">
              {[
                [t.apps, stats.apps],
                [t.minutes, stats.minutes],
                [t.goals, stats.goals],
                [t.assists, stats.assists],
                [t.averageRating, averageRating],
                [t.fitness, `${activePlayer.fitness ?? 0}%`],
                [t.morale, `${activePlayer.morale ?? 0}%`],
                [t.potential, activePlayer.potential ?? '—'],
              ].map(([name, value]) => (
                <div key={String(name)}>
                  <strong>{value}</strong>
                  <span>{name}</span>
                </div>
              ))}
            </section>

            <section className="sheet-info-panel">
              <h2>{t.statistics}</h2>
              {editing ? (
                <div className="sheet-edit-fields">
                  <label>
                    <span>{t.preferredFoot}</span>
                    <select
                      value={String(bio.preferred_foot ?? '')}
                      onChange={(event) => setBioField('preferred_foot', event.target.value)}
                    >
                      <option value="">—</option>
                      <option value="Right">Right</option>
                      <option value="Left">Left</option>
                      <option value="Both">Both</option>
                    </select>
                  </label>
                  <label>
                    <span>{t.height}</span>
                    <input
                      type="number"
                      min="100"
                      max="230"
                      step="0.01"
                      value={String(bio.height ?? '')}
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(event) => setBioField('height', event.target.value === '' ? null : Number(event.target.value))}
                    />
                  </label>
                  <label>
                    <span>{t.weight}</span>
                    <input
                      type="number"
                      min="35"
                      max="180"
                      step="0.1"
                      value={String(bio.weight ?? '')}
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(event) => setBioField('weight', event.target.value === '' ? null : Number(event.target.value))}
                    />
                  </label>
                  <label>
                    <span>{t.form}</span>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      step=".1"
                      value={draft.current_form ?? ''}
                      onFocus={(event) => event.currentTarget.select()}
                      onChange={(event) => setDraft({
                        ...draft,
                        current_form: event.target.value === '' ? null : Number(event.target.value),
                      })}
                    />
                  </label>
                </div>
              ) : (
                <dl>
                  <dt>{t.preferredFoot}</dt><dd>{String(bio.preferred_foot ?? '—')}</dd>
                  <dt>{t.height}</dt><dd>{bio.height ? `${bio.height} cm` : '—'}</dd>
                  <dt>{t.weight}</dt><dd>{bio.weight ? `${bio.weight} kg` : '—'}</dd>
                  <dt>{t.form}</dt><dd>{Number(activePlayer.current_form ?? 0).toFixed(1)}</dd>
                </dl>
              )}
            </section>

            <section className="sheet-notes-panel">
              <h2>{t.coachNotes}</h2>
              {editing ? (
                <textarea
                  className="sheet-notes-editor"
                  value={draft.notes ?? ''}
                  onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
                  placeholder={t.noCoachNotes}
                />
              ) : <p>{activePlayer.notes || t.noCoachNotes}</p>}
            </section>
          </div>
        ) : editing ? (
          <EditableMetricBoard
            values={(() => {
              const stored =
                tab === 'attributes' ? { ...(draft.attributes ?? {}) } :
                tab === 'mental' ? { ...(draft.mindset ?? {}) } :
                { ...(draft.abilities ?? {}) };
              // Empty Supabase objects must still be immediately editable.
              // Render a neutral football template without persisting it until
              // the coach actually changes a value and presses Save Changes.
              return Object.keys(stored).length ? stored : templateFor(tab);
            })()}
            onChange={(next) => {
              if (tab === 'attributes') setDraft({ ...draft, attributes: next });
              else if (tab === 'mental') setDraft({ ...draft, mindset: next });
              else setDraft({ ...draft, abilities: next });
            }}
            emptyText={t.noData}
          />
        ) : (
          <section className="attribute-board">
            {currentEntries.length ? currentEntries.map(([name, value]) => (
              <div className="attribute-row" key={name}>
                <span>{label(name)}</span>
                <div><i style={{ width: `${Math.min(100, Number(value))}%` }} /></div>
                <strong>{value}</strong>
              </div>
            )) : <p>{t.noData}</p>}
          </section>
        )}

        {saveMessage && (
          <p className={`player-save-message ${saveMessage === t.playerUpdated ? 'success' : ''}`}>
            {saveMessage === t.playerUpdated && <Check size={15} />}
            {saveMessage}
          </p>
        )}

        <footer className="player-sheet-management">
          {!confirmDelete ? (
            <button className="remove-player-button" type="button" disabled={editing} onClick={() => {
              setDeleteMessage('');
              setConfirmDelete(true);
            }}>
              <Trash2 size={16} />
              {t.removePlayer}
            </button>
          ) : (
            <div className="remove-player-confirm">
              <div>
                <strong>{t.removePlayerConfirm}</strong>
                <small>{t.removePlayerWarning}</small>
              </div>
              <button className="steel-button" type="button" onClick={() => setConfirmDelete(false)}>
                {t.cancel}
              </button>
              <button
                className="remove-player-danger"
                type="button"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  setDeleteMessage('');
                  const result = await deletePlayer(activePlayer.id);
                  setDeleting(false);
                  if (!result.ok) {
                    setDeleteMessage(result.message);
                    return;
                  }
                  onClose();
                }}
              >
                <Trash2 size={15} />
                {deleting ? `${t.removing}…` : t.removePlayer}
              </button>
            </div>
          )}
          {deleteMessage && <p className="error-text">{deleteMessage}</p>}
        </footer>
      </article>
      {photoViewerOpen && activePlayer.photo_url && (
        <div className="player-photo-lightbox" role="dialog" aria-modal="true" aria-label={`${activePlayer.name} photo`} onClick={() => setPhotoViewerOpen(false)}>
          <div className="player-photo-lightbox-stage" onClick={(event) => event.stopPropagation()}>
            <button type="button" className="player-photo-lightbox-close" onClick={() => setPhotoViewerOpen(false)} aria-label="Close photo"><X size={22}/></button>
            <img src={activePlayer.photo_url} alt={activePlayer.name} />
            <div className="player-photo-lightbox-caption"><strong>{activePlayer.name}</strong><span>#{activePlayer.squad_number ?? '—'} · {activePlayer.position || '—'}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
